import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useCallback, useRef, useState } from 'react'
import {
  BaseCriteriaAssignmentForm,
  FormSubmitValues,
} from '~/features/criteria-forms/base-criteria-assignment-form'
import { formatDateTime, formatDuration } from '~/helpers/dateHelper'
import { SeedMutators } from '~/mutators/seeds'
import { useTRPC, useTRPCClient } from '~/services/trpc'

import { Button, Heading1, Heading2, Icon, Multiline } from '@hap/components'
import { versionedScaleOptions } from '@hap/constants'

interface EvaluationResult {
  interactionId: string
  contactLogId: string
  score: number | null
  reasoning: string
  status: string
}

interface RefineSuggestions {
  improvedDefinition: string
  improvedSuggestedVerbiage: string[]
  improvedScoreGuidance: Record<string, string>
  summary: string
  rationale: string[]
}

export const Route = createFileRoute(
  '/_authenticated/$alias/v2/scorecard/$scorecardId/criteria/$criteriaId',
)({
  component: RouteComponent,
})

function RouteComponent() {
  const params = Route.useParams()
  const trpc = useTRPC()
  const { data: assignment } = useQuery(
    trpc.scorecardCriteria.getAssignmentById.queryOptions(params.criteriaId),
  )

  if (!assignment) return <>Loading...</>

  return (
    <div className="grid w-full gap-10 p-4">
      <Heading1>{assignment.criterion.name}</Heading1>

      <Heading2>Criterion</Heading2>

      <Multiline>
        {assignment.definition || assignment.criterion.defaultDefinition}
      </Multiline>

      <CriteriaSeedOverview
        scorecardId={params.scorecardId}
        criteriaId={params.criteriaId}
        scale={assignment.criterion.scale}
        assignment={assignment}
      />
    </div>
  )
}

function CriteriaSeedOverview({
  scorecardId,
  criteriaId,
  scale,
  assignment,
}: {
  scorecardId: string
  criteriaId: string
  scale: string
  assignment: {
    id: string
    sectionId: string
    weight: number | null
    definition: string | null
    suggestedVerbiage: string[] | null
    scoreGuidance: Record<string, Record<string, string>> | null
    resources: string[] | null
    documentation: boolean | null
    autoFail: boolean | null
    criterion: {
      id: string
      name: string
      scale: string
      auto: boolean
      defaultDefinition: string
      defaultScoreGuidance: Record<string, Record<string, string>>
    }
  }
}) {
  const trpc = useTRPC()
  const trpcClient = useTRPCClient()
  const queryClient = useQueryClient()

  const { data: seed, isLoading: isSeedLoading } = useQuery(
    trpc.seeds.getOrCreateByScorecardId.queryOptions(scorecardId),
  )

  const { data: interactions, isLoading: isInteractionsLoading } = useQuery(
    trpc.seeds.listInteractions.queryOptions(
      { seedId: seed?.id ?? '', filters: { criteriaId } },
      {
        enabled: Boolean(seed?.id),
      },
    ),
  )

  const [isEvaluating, setIsEvaluating] = useState(false)
  const [results, setResults] = useState<Record<string, EvaluationResult>>({})
  const resultsRef = useRef<Record<string, EvaluationResult>>({})
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null)

  const [refineState, setRefineState] = useState<{
    status: 'idle' | 'analyzing' | 'synthesizing' | 'complete' | 'error'
    suggestions?: RefineSuggestions
  }>({ status: 'idle' })
  const refineSubscriptionRef = useRef<{ unsubscribe: () => void } | null>(null)

  const [evaluationComplete, setEvaluationComplete] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)

  const updateMutation = useMutation(
    trpc.scorecardCriteriaAssignments.set.mutationOptions({
      onSettled: () => {
        queryClient.invalidateQueries(
          trpc.scorecardCriteria.getAssignmentById.queryOptions(criteriaId),
        )
        queryClient.invalidateQueries(trpc.scorecards.getById.queryOptions(scorecardId))
        queryClient.invalidateQueries(trpc.lookup.pathFilter())
      },
    }),
  )

  const startEvaluation = useCallback(() => {
    if (!seed) return

    setIsEvaluating(true)
    setResults({})
    resultsRef.current = {}
    setRefineState({ status: 'idle' })
    setShowEditForm(false)
    setEvaluationComplete(false)

    subscriptionRef.current = trpcClient.evaluate.criteria.subscribe(
      { criteriaId, seedId: seed.id },
      {
        onData: (data) => {
          setResults((prev) => {
            const next = { ...prev, [data.contactLogId]: data }
            resultsRef.current = next
            return next
          })
        },
        onError: (err) => {
          console.error('Evaluation error:', err)
          setIsEvaluating(false)
          setEvaluationComplete(true)
        },
        onComplete: () => {
          setIsEvaluating(false)
          setEvaluationComplete(true)
        },
      },
    )
  }, [criteriaId, seed, trpcClient])

  const startRefinement = useCallback(() => {
    const currentResults = resultsRef.current
    if (!interactions) return

    const refineInteractions = interactions
      .filter(
        (i) =>
          i.interactionId &&
          i.contactLogId &&
          currentResults[i.contactLogId]?.status === 'complete',
      )
      .map((i) => ({
        interactionId: i.interactionId!,
        contactLogId: i.contactLogId!,
        autoScore: {
          awardedPoints: currentResults[i.contactLogId!]!.score ?? 0,
          notes: currentResults[i.contactLogId!]!.reasoning,
        },
        baselinePoints: (i as any).awardedPoints ?? 0,
      }))

    if (refineInteractions.length === 0) return

    setRefineState({ status: 'analyzing' })
    setShowEditForm(false)

    refineSubscriptionRef.current = trpcClient.evaluate.refineCriteria.subscribe(
      { criteriaId, interactions: refineInteractions },
      {
        onData: (data) => {
          if (data.status === 'analyzing') {
            setRefineState({ status: 'analyzing' })
          } else if (data.status === 'synthesizing') {
            setRefineState({ status: 'synthesizing' })
          } else if (data.status === 'complete') {
            setRefineState({
              status: 'complete',
              suggestions: data.suggestions,
            })
          }
        },
        onError: (err) => {
          console.error('Refine error:', err)
          setRefineState({ status: 'error' })
        },
        onComplete: () => {
          // No-op; state already updated via onData
        },
      },
    )
  }, [criteriaId, interactions, trpcClient])

  const stopEvaluation = useCallback(() => {
    subscriptionRef.current?.unsubscribe()
    subscriptionRef.current = null
    refineSubscriptionRef.current?.unsubscribe()
    refineSubscriptionRef.current = null
    setIsEvaluating(false)
    setEvaluationComplete(false)
    setRefineState({ status: 'idle' })
    setShowEditForm(false)
  }, [])

  const handleApplySuggestions = useCallback(
    async (values: FormSubmitValues) => {
      await updateMutation.mutateAsync({
        id: assignment.id,
        values: {
          ...values,
          scorecardId,
          sectionId: assignment.sectionId,
        },
      })
      setShowEditForm(false)
    },
    [updateMutation, assignment.id, scorecardId, assignment.sectionId],
  )

  if (isSeedLoading || isInteractionsLoading) return <>Loading...</>

  const suggestionsDefaultValues = refineState.suggestions
    ? {
        name: assignment.criterion.name,
        scale: assignment.criterion.scale as any,
        weight: assignment.weight ?? 1,
        auto: assignment.criterion.auto,
        autoFail: assignment.autoFail ?? false,
        definition: refineState.suggestions.improvedDefinition,
        suggestedVerbiage: refineState.suggestions.improvedSuggestedVerbiage,
        resources: assignment.resources ?? [],
        documentation: assignment.documentation ?? false,
        scoreGuidance: (() => {
          const guidance: Record<string, Record<string, string>> = {}
          const ai = refineState.suggestions!.improvedScoreGuidance
          const scaleGuidance: Record<string, string> = {}
          let naValue = ''

          for (const [key, value] of Object.entries(ai)) {
            const lower = key.toLowerCase()
            if (lower === 'na' || lower === 'n/a' || lower === 'not applicable') {
              naValue = value
            } else {
              scaleGuidance[key] = value
            }
          }

          guidance[scale] = scaleGuidance
          if (naValue) {
            guidance.na = { na: naValue }
          }
          return guidance
        })(),
      }
    : undefined

  return (
    <div className="w-full space-y-2">
      <header className="mb-6 flex items-center gap-4">
        <hgroup className="shrink grow">
          <Heading2>Seed Overview</Heading2>
          <p>{seed?.id}</p>
        </hgroup>
        <div className="shrink-0">
          <Button
            variant="lime"
            disabled={isEvaluating || !interactions?.length}
            onClick={startEvaluation}
          >
            {isEvaluating ? 'Evaluating...' : 'Test Changes'}
          </Button>
          {isEvaluating && (
            <Button variant="ghost" onClick={stopEvaluation} className="ml-2">
              Stop
            </Button>
          )}
          {evaluationComplete &&
            refineState.status === 'idle' &&
            Object.values(results).some((r) => r.status === 'complete') && (
              <Button variant="lime" onClick={startRefinement} className="ml-2">
                Refine Criteria
              </Button>
            )}
          {refineState.status === 'analyzing' && (
            <span className="ml-2 inline-flex items-center gap-1 text-sm">
              <Icon name="arrows-clockwise" className="size-3 animate-spin" />
              Refining...
            </span>
          )}
          {refineState.status === 'synthesizing' && (
            <span className="ml-2 inline-flex items-center gap-1 text-sm">
              <Icon name="arrows-clockwise" className="size-3 animate-spin" />
              Synthesizing...
            </span>
          )}
        </div>
      </header>
      {interactions?.map((interaction) => (
        <InteractionItem
          key={interaction.interactionId}
          interaction={interaction as any}
          seedId={seed?.id ?? ''}
          scale={scale}
          result={
            interaction.contactLogId ? results[interaction.contactLogId] : undefined
          }
          isEvaluating={isEvaluating}
        />
      ))}

      {refineState.status !== 'idle' && (
        <div className="border-foreground/50 mt-6 rounded border p-4">
          <Heading2>Suggested Improvements</Heading2>
          {refineState.status === 'analyzing' && (
            <p className="text-muted-foreground mt-2 text-sm">
              Analyzing interactions...
            </p>
          )}
          {refineState.status === 'synthesizing' && (
            <p className="text-muted-foreground mt-2 text-sm">
              Synthesizing improvements...
            </p>
          )}
          {refineState.status === 'error' && (
            <p className="mt-2 text-sm text-red-500">
              Failed to generate suggestions. Please try again.
            </p>
          )}
          {refineState.status === 'complete' && refineState.suggestions && (
            <div className="mt-4 space-y-4">
              {!showEditForm && (
                <>
                  <div>
                    <h3 className="text-sm font-semibold">Summary</h3>
                    <p className="text-muted-foreground text-sm">
                      {refineState.suggestions.summary}
                    </p>
                  </div>
                  {refineState.suggestions.rationale.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold">Rationale</h3>
                      <ul className="text-muted-foreground list-disc pl-5 text-sm">
                        {refineState.suggestions.rationale.map((r, i) => (
                          <li key={i}>{r}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div>
                    <h3 className="text-sm font-semibold">Improved Definition</h3>
                    <p className="text-muted-foreground text-sm">
                      {refineState.suggestions.improvedDefinition}
                    </p>
                  </div>
                  {refineState.suggestions.improvedSuggestedVerbiage.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold">
                        Improved Suggested Verbiage
                      </h3>
                      <ul className="text-muted-foreground list-disc pl-5 text-sm">
                        {refineState.suggestions.improvedSuggestedVerbiage.map((v, i) => (
                          <li key={i}>{v}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {Object.keys(refineState.suggestions.improvedScoreGuidance).length >
                    0 && (
                    <div>
                      <h3 className="text-sm font-semibold">Improved Score Guidance</h3>
                      <ul className="text-muted-foreground list-disc pl-5 text-sm">
                        {Object.entries(
                          refineState.suggestions.improvedScoreGuidance,
                        ).map(([score, guidance], i) => (
                          <li key={i}>
                            <span className="font-medium">{score}:</span> {guidance}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <Button variant="lime" onClick={() => setShowEditForm(true)}>
                    Edit & Apply
                  </Button>
                </>
              )}
              {showEditForm && suggestionsDefaultValues && (
                <div className="mt-4">
                  <BaseCriteriaAssignmentForm
                    onSubmit={handleApplySuggestions}
                    onCancel={() => setShowEditForm(false)}
                    defaultDefinition={assignment.criterion.defaultDefinition}
                    disableScale
                    criteriaId={assignment.criterion.id}
                    defaultValues={suggestionsDefaultValues}
                    submitLabel="Apply Improvements"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function InteractionItem({
  interaction,
  seedId,
  result,
  isEvaluating,
  scale,
}: {
  interaction: {
    interactionId: string | null
    seedId: string | null
    addedAt: string | null
    contactLogId: string | null
    startedAt: string | null
    handledBy: string | null
    method: string | null
    recordingDuration: number | null
    totalTime: number | null
    summary: string | null

    awardedPoints: number | null
    possiblePoints: number | null
  }
  scale: string
  seedId: string
  result?: EvaluationResult
  isEvaluating: boolean
}) {
  const { remove, isPending } = SeedMutators.useRemoveInteraction({ seedId })

  const duration = interaction.recordingDuration ?? interaction.totalTime ?? 0
  const aiScore = result?.score ?? null
  const reasoning = result?.reasoning ?? ''
  const scaleLabels =
    versionedScaleOptions[20250231][
      scale as keyof (typeof versionedScaleOptions)[20250231]
    ]

  return (
    <div className="border-foreground/50 flex items-stretch border">
      <div
        className={`border-foreground/50 w-3 shrink-0 border-r ${
          aiScore !== null
            ? aiScore === interaction.awardedPoints
              ? 'bg-lime-green'
              : 'bg-red-500'
            : 'bg-foreground/10'
        }`}
      />
      <div className="flex flex-1 items-center gap-3 p-3">
        <div className="min-w-0 shrink grow space-y-1">
          <div className="flex items-center gap-2">
            {interaction.contactLogId && (
              <Link
                to="/$alias/interaction/$contactLogId"
                params={{ contactLogId: interaction.contactLogId }}
                from="/$alias"
                className="truncate text-sm font-medium hover:underline"
                target="_blank"
              >
                {interaction.contactLogId}
              </Link>
            )}
            <span className="text-muted-foreground">
              {interaction.startedAt ? formatDateTime(interaction.startedAt) : '—'}
            </span>
          </div>
          <div className="text-muted-foreground flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Icon name="user-circle" className="size-3" />
              {interaction.handledBy ?? 'Unknown'}
            </span>
            <span className="flex items-center gap-1">
              <Icon name="phone" className="size-3" />
              {interaction.method ?? '—'}
            </span>
            <span className="flex items-center gap-1">
              <Icon name="timer" className="size-3" />
              {formatDuration(duration)}
            </span>
          </div>

          {reasoning && <p className="text-muted-foreground text-xs">{reasoning}</p>}
        </div>
        <div className="w-24 shrink-0 text-center text-sm">
          {isEvaluating && result?.status === 'evaluating' ? (
            <span className="flex items-center justify-center gap-1">
              <Icon name="arrows-clockwise" className="size-3 animate-spin" />
              AI...
            </span>
          ) : aiScore !== null ? (
            <span className="font-semibold">
              AI:{' '}
              {scaleLabels?.find(
                (option: { value: number; label: string }) => option.value === aiScore,
              )?.label || 'N/A'}
            </span>
          ) : (
            <span className="text-muted-foreground">AI: —</span>
          )}
        </div>
        <div className="w-24 shrink-0 text-center text-sm">
          <span className="text-muted-foreground">
            Human:{' '}
            {scaleLabels?.find(
              (option: { value: number; label: string }) =>
                option.value === interaction.awardedPoints,
            )?.label || 'N/A'}
          </span>
        </div>
        <Button
          size="icon-sm"
          variant="ghost"
          icon="trash"
          disabled={isPending}
          onClick={() => {
            if (interaction.interactionId) {
              remove(interaction.interactionId)
            }
          }}
        />
      </div>
    </div>
  )
}
